import { TranslationsObject } from "@genericTypes/sharedTypes";
import { GQLPosts, GQLUrls } from "@gql/types/gql";
import { translate } from "@helpers/translationObjectFormatter";
import {
    resolveNewsTagsColor,
    resolveURL,
    resolveNewsTagName,
} from "@lib/sharedUtils";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import Badge from "@components/shared/badge/Badge";
import Button from "react-multi-date-picker/components/button";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { titleDecoder } from "@lib/sharedUtils";

export const DotButton = ({
    selected,
    onClick,
}: {
    selected: boolean;
    onClick: () => void;
}) => (
    <button
        className={`embla__dot ${selected ? "is-selected" : ""}`}
        type="button"
        onClick={onClick}
    />
);

const TrendingNews = () => {
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: trendingNews } = useQuery<GQLPosts[]>(["trendingNews"]);
    const [emblaRef, embla] = useEmblaCarousel({ loop: true }, [Autoplay()]);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const scrollTo = useCallback(
        (index: number) => embla && embla.scrollTo(index),
        [embla],
    );
    const onSelect = useCallback(() => {
        if (!embla) return;
        setSelectedIndex(embla.selectedScrollSnap());
    }, [embla, setSelectedIndex]);

    useEffect(() => {
        if (!embla) return;
        onSelect();
        setScrollSnaps(embla.scrollSnapList());
        embla.on("select", onSelect);
    }, [embla, setScrollSnaps, onSelect]);

    return (
        <div className="relative flex flex-col gap-8">
            <h2 className="font-bold text-4xl">
                {translate(translations, "several_news_trending")}
            </h2>
            <div className="w-[100%] overflow-hidden" ref={emblaRef}>
                <div className="flex gap-8">
                    {trendingNews?.map((item, index, self) => {
                        return (
                            <a
                                href={resolveURL(item.url as GQLUrls)}
                                className={`relative w-full max-w-[630px] min-w-[230px] sm:h-[380px] flex items-end embla__slide hover:no-underline ${
                                    index === self.length - 1 ? "mr-8" : ""
                                }`}
                                key={item.title}
                            >
                                <div
                                    className={`${styles["trending-image"]} w-full h-full absolute z-20`}
                                />
                                <div className="p-4 sm:p-8 z-[999] flex flex-col gap-4">
                                    {item.tags && item.tags.length > 0 && (
                                        <Badge
                                            text={
                                                resolveNewsTagName(
                                                    item?.tags as {
                                                        name: string;
                                                    }[],
                                                ) as string
                                            }
                                            variant={resolveNewsTagsColor(
                                                (resolveNewsTagName(
                                                    item?.tags as {
                                                        name: string;
                                                    }[],
                                                ) as string) ?? "",
                                            )}
                                            className="h-fit capitalize text-sm"
                                        />
                                    )}
                                    <span className="text-white text-sm sm:text-2xl font-bold">
                                        {titleDecoder(item.title ?? "")}
                                    </span>
                                    <Button className="bg-transparent border hover:bg-transparent w-fit px-6 sm:px-8 sm:py-2 text-white rounded">
                                        {translate(
                                            translations,
                                            "several_general_readMoreButton",
                                        )}
                                    </Button>
                                </div>
                                <img
                                    src={item.image?.fullPath as string}
                                    alt={titleDecoder(item.title ?? "")}
                                    className={`absolute w-full h-full right-0 `}
                                />
                            </a>
                        );
                    })}
                </div>
            </div>
            <div className="embla__dots">
                {scrollSnaps.map((_, index) => (
                    <DotButton
                        key={index}
                        selected={index === selectedIndex}
                        onClick={() => scrollTo(index)}
                    />
                ))}
            </div>
        </div>
    );
};

export default TrendingNews;
